import { useState } from 'react'
import { enqueueSnackbar } from 'notistack'
import AddIcon from '@mui/icons-material/Add'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import QuoteForm, { QuoteFormData } from '@components/molecules/QuoteForm'
import QuoteListItem from '@components/organisms/QuoteListItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SxProps, Theme } from '@mui/material/styles'
import useQuotes from '@utils/useQuotes'
import useCurrentUser from '@utils/useCurrentUser'
import {
  LegalMatterQuotesFragment,
  QuoteItemType,
  LegalMatterQuoteFragment,
  Permission,
  Quote,
  QuoteStatus,
} from '@generatedTypes/graphql'
import AclIdentity from '@/models/AclIdentity'
import DotMenu, { DotMenuAnchor } from '../atoms/DotMenu'
import AclEntity from '@/models/AclEntity'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import ConfirmationDialog, {
  ConfirmationDialogProps,
} from '../molecules/ConfirmationDialog'
import log from 'loglevel'
import { toCents, toDollars } from '@/utils/stripeUtils'
import IdChip from '../atoms/IdChip'

interface LawyerQuoteAdminProps {
  currentUserAclIdentities?: AclIdentity[]
  legalMatterKey: string | undefined
  fragment: LegalMatterQuotesFragment
  readOnly?: boolean
  sx?: SxProps<Theme>
}
interface QuoteDotMenuAnchor extends DotMenuAnchor {
  quote: LegalMatterQuoteFragment
}

const LawyerQuoteAdmin: React.FC<LawyerQuoteAdminProps> = ({
  currentUserAclIdentities = [],
  legalMatterKey,
  fragment,
  readOnly = false,
  sx = {},
}) => {
  const quotes = fragment.quotes.edges.map((edge) => edge.node)
  const [quoteFormVisible, setQuoteFormVisible] = useState(false)
  const { createQuote, sendQuote, deleteQuote, updateQuote, quoteLoading } =
    useQuotes(legalMatterKey)
  const { user } = useCurrentUser()

  const dialogConfigInitialState: ConfirmationDialogProps = {
    title: <></>,
    content: <></>,
    actions: <></>,
    open: false,
  }
  const [dialogConfig, setDialogConfig] = useState<ConfirmationDialogProps>(
    dialogConfigInitialState,
  )

  const handleQuoteSend = (quoteKey: string): void => {
    void sendQuote({
      variables: {
        quoteKey,
      },
      onCompleted: (data) => {
        if (data.quoteSend.success) {
          enqueueSnackbar('Quote sent successfully', {
            variant: 'success',
          })
        } else {
          enqueueSnackbar(
            `There was a problem in sending the quote: ${data.quoteSend.message}`,
            {
              variant: 'error',
            },
          )
        }
      },
      onError: () => {
        enqueueSnackbar(
          'There was a problem in processing the request, try again in a few seconds',
          {
            variant: 'error',
          },
        )
      },
    })
  }

  const handleQuoteSubmit = (data: QuoteFormData): Promise<boolean> => {
    const quoteItem =
      data.formType === QuoteItemType.Hourly
        ? {
            type: QuoteItemType.Hourly,
            hourlyRate: toCents(data.hourlyRate),
            payableAmount: toCents(data.payableAmount),
          }
        : {
            type: QuoteItemType.Flat,
            payableAmount: toCents(data.payableAmount),
            filingAndOtherFees: data.filingAndOtherFees
              ? toCents(data.filingAndOtherFees)
              : 0,
          }
    return new Promise((resetQuoteForm) => {
      void createQuote({
        variables: {
          quoteData: {
            legalMatterKey,
            details: data.details,
            items: [quoteItem],
          },
        },
        onCompleted: (data) => {
          if (data.quoteCreate.__typename === 'QuoteMutationResponse') {
            enqueueSnackbar('Quote created successfully', {
              variant: 'success',
            })
            setQuoteFormVisible(false)
            return resetQuoteForm(true)
          } else {
            enqueueSnackbar(
              `There was a problem in creating the quote: ${data.quoteCreate.message}`,
              {
                variant: 'error',
              },
            )
            return resetQuoteForm(false)
          }
        },
        onError: (err) => {
          enqueueSnackbar(
            'There was a problem in processing the request, try again in a few seconds',
            {
              variant: 'error',
            },
          )
          return resetQuoteForm(false)
        },
      })
    })
  }

  const handleQuoteEdit = (
    data: QuoteFormData,
    quoteKey: string,
  ): Promise<boolean> => {
    const quoteItem =
      data.formType === QuoteItemType.Hourly
        ? {
            type: QuoteItemType.Hourly,
            hourlyRate: toCents(data.hourlyRate),
            payableAmount: toCents(data.payableAmount),
          }
        : {
            type: QuoteItemType.Flat,
            payableAmount: toCents(data.payableAmount),
            filingAndOtherFees: data.filingAndOtherFees
              ? toCents(data.filingAndOtherFees)
              : 0,
          }
    return new Promise((resetQuoteForm) => {
      void updateQuote({
        variables: {
          quoteData: {
            quoteKey: quoteKey,
            items: [
              {
                hourlyRate: quoteItem?.hourlyRate,
                payableAmount: quoteItem?.payableAmount,
                filingAndOtherFees: quoteItem?.filingAndOtherFees,
                type: quoteItem.type,
              },
            ],
            details: data.details,
          },
        },
        onCompleted: (data) => {
          if (data.quoteUpdate.__typename === 'QuoteMutationResponse') {
            enqueueSnackbar('Quote updated successfully', {
              variant: 'success',
            })
            setQuoteFormVisible(false)
            onRemoveQuotesInEditMode(quoteKey)
            return resetQuoteForm(true)
          } else {
            enqueueSnackbar(
              `There was a problem updating the quote: ${data.quoteUpdate.message}`,
              {
                variant: 'error',
              },
            )
            return resetQuoteForm(false)
          }
        },
        onError: (err) => {
          enqueueSnackbar(
            'There was a problem in processing the request, try again in a few seconds',
            {
              variant: 'error',
            },
          )
          return resetQuoteForm(false)
        },
      })
    })
  }

  const [dotMenuAnchor, setDotMenuAnchor] = useState<QuoteDotMenuAnchor | null>(
    null,
  )

  const [quotesInEditMode, setQuotesInEditMode] = useState<
    Record<string, Quote>
  >({})

  const onRemoveQuotesInEditMode = (key: string): void => {
    const { [key]: _, ...filtered } = quotesInEditMode
    setQuotesInEditMode(filtered)
  }

  const canQuoteBeEditedOrDeleted = (quote: Quote | null) => {
    return (
      quote != null &&
      quote.status !== QuoteStatus.AcceptedPaid &&
      quote.status !== QuoteStatus.AcceptedPaymentFailed &&
      quote.status !== QuoteStatus.AcceptedPaymentPending &&
      quote.status !== QuoteStatus.Sent
    )
  }

  const quotesDotMenuOptions = [
    {
      action: 'editQuote',
      label: 'Edit Quote',
      icon: <EditIcon />,
      requiredPermission: Permission.QuotesDelete,
      isVisible: (quote) => canQuoteBeEditedOrDeleted(quote),
    },
    {
      action: 'deleteQuote',
      label: 'Delete Quote',
      icon: <DeleteIcon />,
      requiredPermission: Permission.QuotesUpdate,
      isVisible: (quote) => canQuoteBeEditedOrDeleted(quote),
    },
  ]

  const handleQuoteDotMenuClick = (
    event: React.MouseEvent,
    quote: LegalMatterQuoteFragment,
  ): void => {
    const quoteAcl = new AclEntity(quote.acl)
    setDotMenuAnchor({
      quote: quote,
      element: event.currentTarget,
      menuItems: quotesDotMenuOptions.map((menuOpt) => ({
        ...menuOpt,
        visible: menuOpt.isVisible(quote),
        disabled: !quoteAcl.allows(
          user.aclIdentities,
          menuOpt.requiredPermission,
        ),
      })),
    })
  }

  const handleDialogActionConfirm = (action: string): void => {
    switch (action) {
      case 'deleteQuote':
        if (dotMenuAnchor == null) return
        void deleteQuote({
          variables: {
            quoteKey: dotMenuAnchor.quote.key,
          },
          onCompleted: (data) => {
            setDialogConfig(dialogConfigInitialState)
            if (data.quoteDelete.success) {
              enqueueSnackbar('Quote deleted successfully', {
                variant: 'success',
              })
            } else {
              enqueueSnackbar(
                `There was an error while deleting the quote: ${data.quoteDelete.message}, try again in a few seconds`,
                {
                  variant: 'error',
                },
              )
            }
          },
          onError: () => {
            setDialogConfig(dialogConfigInitialState)
            enqueueSnackbar('There was a problem in deleting the quote', {
              variant: 'error',
            })
          },
        })
        break
    }
  }

  const onClickDotMenuOption = (action: string): void => {
    switch (action) {
      case 'deleteQuote':
        setDialogConfig((prevState) => ({
          ...prevState,
          open: true,
          title: (
            <>
              <Typography variant="h1" gutterBottom>
                Are you sure you want to proceed?
              </Typography>
            </>
          ),
          content: (
            <>
              <Typography variant="h4" gutterBottom>
                You are about to delete this quote:{' '}
                <IdChip id={dotMenuAnchor?.quote.key} />
              </Typography>
              {dialogConfig.errorMessage !== undefined && (
                <Alert variant="outlined" severity="error">
                  {dialogConfig.errorMessage}
                </Alert>
              )}
            </>
          ),
          actions: (
            <>
              <Button
                onClick={() => {
                  setDialogConfig(dialogConfigInitialState)
                }}
                loading={quoteLoading}
                autoFocus
                variant="contained"
                color="error"
                data-testid="confirm-dialog-btn-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  handleDialogActionConfirm(action)
                }}
                autoFocus
                variant="contained"
                color="primary"
                data-testid="confirm-dialog-btn-accept"
              >
                Confirm
              </Button>
            </>
          ),
        }))
        break
      case 'editQuote':
        if (dotMenuAnchor == null) return
        setQuotesInEditMode({
          ...quotesInEditMode,
          [dotMenuAnchor?.quote.key]: dotMenuAnchor?.quote,
        })
        break

      default:
    }
  }

  return (
    <Box sx={{ ...sx }}>
      <Stack justifyContent="space-between" direction="row" alignItems="center">
        <Typography variant="h2" gutterBottom>
          Quote
        </Typography>
        {!readOnly && (
          <Button
            variant="outlined"
            color="primary"
            data-testid="quote-add-button"
            disabled={readOnly}
            startIcon={<AddIcon />}
            onClick={() => setQuoteFormVisible(true)}
          >
            Add Quote
          </Button>
        )}
      </Stack>
      <Divider sx={{ my: 2, mb: 1 }} />
      {quoteFormVisible && (
        <QuoteForm
          onSubmit={handleQuoteSubmit}
          onClickCancelButton={() => setQuoteFormVisible(false)}
        />
      )}
      {(quotes?.length == null || quotes.length == 0) && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" icon={false}>
            <Typography variant="h5" gutterBottom>
              No quotes have been created yet.
            </Typography>
            <Typography variant="body1" gutterBottom>
              You can create quotes to bill your clients for legal services.
            </Typography>
          </Alert>
        </Box>
      )}
      {quotes?.length > 0 &&
        quotes.map((quote) => (
          <QuoteListItem
            key={quote.key}
            currentUserAclIdentities={currentUserAclIdentities}
            quote={quote}
            readOnly={readOnly}
            onClickSend={handleQuoteSend}
            onClickMenu={
              !readOnly && canQuoteBeEditedOrDeleted(quote as Quote)
                ? (event) => {
                    handleQuoteDotMenuClick(event, quote)
                  }
                : undefined
            }
            inEditMode={quote.key in quotesInEditMode}
            isSending={quoteLoading}
            editForm={
              <QuoteForm
                onSubmit={(data) => handleQuoteEdit(data, quote.key)}
                quoteData={
                  quote.items[0].type === QuoteItemType.Hourly
                    ? {
                        formType: quote.items[0].type,
                        hourlyRate: quote.items[0]?.hourlyRate
                          ? toDollars(quote.items[0].hourlyRate)
                          : 0,
                        payableAmount: toDollars(quote.items[0].payableAmount),
                        details: quote.details,
                      }
                    : {
                        formType: quote.items[0].type,
                        payableAmount: toDollars(quote.items[0].payableAmount),
                        filingAndOtherFees: quote.items[0]?.filingAndOtherFees
                          ? toDollars(quote.items[0].filingAndOtherFees)
                          : 0,
                        details: quote.details,
                      }
                }
                onClickCancelButton={() => {
                  onRemoveQuotesInEditMode(quote.key)
                }}
              />
            }
          />
        ))}
      <DotMenu
        anchor={dotMenuAnchor}
        open={Boolean(dotMenuAnchor)}
        onItemSelected={(action) => {
          setDotMenuAnchor(null)
          onClickDotMenuOption(action)
        }}
        onClose={() => {
          setDotMenuAnchor(null)
        }}
      />
      <ConfirmationDialog
        open={dialogConfig.open}
        onClose={() => {
          setDialogConfig(dialogConfigInitialState)
        }}
        title={dialogConfig.title}
        content={dialogConfig.content}
        actions={dialogConfig.actions}
        errorMessage={dialogConfig?.errorMessage}
      />
    </Box>
  )
}

export default LawyerQuoteAdmin
